import React from 'react'
import { observer } from 'mobx-react-lite'
import { isAfter } from 'date-fns/isAfter'
import { isBefore } from 'date-fns/isBefore'
import { isToday } from 'date-fns/isToday'

import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'

import { Icon, Text } from '@mxenabled/mxui'

import {
  Drawer,
  InformationTooltip,
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
  useWidgetContainerProvider,
} from '../../common'
import { type Recurrence } from '../../core'

import { ManageIncome } from '../../recurringtransactions'

import CashflowAmountHeading from './CashflowAmountHeading'
import { useCashflowUiStore } from '../context/hooks'
import { buildDynamicCopy } from '../../core/utils/Localization'

// This filter only includes activities that have an account guid that matches an account guid
// in the given list of accountGuids and is within the given date range
const recurrenceAccountFilter =
  (accountGuids: string[], dateRange: { start: Date; end: Date }) => (r: Recurrence) =>
    r.accountGuid &&
    accountGuids.includes(r.accountGuid) &&
    (isToday(r.expectedDate) || isAfter(r.expectedDate, dateRange.start)) &&
    isBefore(r.expectedDate, dateRange.end)

const CashflowHeading: React.FC = () => {
  const { availableWidth } = useWidgetContainerProvider()
  const { cashflow: customCopy } = useGlobalCopyStore()

  const { isDataLoaded } = useRecurringTransactionsStore()
  const { selectedAccountGuids } = useGlobalUiStore()
  const {
    dateRange,
    income,
    selectedCashAccounts,
    selectedCashBalance,
    upcomingExpenses,
    upcomingIncome,
  } = useCashflowUiStore()

  const [isOpen, setIsOpen] = React.useState(false)

  // Plain derivations: observer tracks the observable reads, so no useMemo is
  // needed. The account-scoped balance is a store computed (selectedCashBalance).
  const filteredExpenses = upcomingExpenses.filter(
    recurrenceAccountFilter(selectedAccountGuids, dateRange),
  )

  const filteredIncome = upcomingIncome.filter(
    recurrenceAccountFilter(selectedAccountGuids, dateRange),
  )

  const cashBalance = selectedCashBalance

  const totalUpcomingExpenses = filteredExpenses.reduce((acc, curr) => acc + curr.amount, 0)
  const totalUpcomingIncome = filteredIncome.reduce((acc, curr) => acc + curr.amount, 0)
  const availableToSpend = cashBalance - totalUpcomingExpenses + totalUpcomingIncome

  const hasRecurringIncome = isDataLoaded && income.length > 0
  const isMobile = availableWidth < 725

  return (
    <Stack sx={{ mx: isMobile ? 16 : 0, my: isMobile ? 24 : 42 }}>
      <Stack
        border={isMobile ? '1px solid' : 'none'}
        borderColor="neutral.light"
        borderRadius={2}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 0 : 24}
        justifyContent="center"
      >
        <CashflowAmountHeading
          amount={cashBalance}
          isMobile={isMobile}
          label={buildDynamicCopy(customCopy.cash_and_bank, selectedCashAccounts.length) as string}
        />

        {isMobile && <Divider />}
        {hasRecurringIncome ? (
          <>
            {!isMobile && <Text sx={{ fontSize: 25, lineHeight: 1 }}>+</Text>}
            <CashflowAmountHeading
              amount={totalUpcomingIncome}
              isMobile={isMobile}
              label={customCopy.to_be_received}
              symbol={isMobile ? '+' : undefined}
            />
          </>
        ) : (
          <Button
            onClick={() => setIsOpen(true)}
            startIcon={<Icon name="add" />}
            sx={{ fontSize: 13 }}
          >
            {customCopy.add_income}
          </Button>
        )}

        {isMobile ? <Divider /> : <Text sx={{ fontSize: 25, lineHeight: 1 }}>-</Text>}

        <CashflowAmountHeading
          amount={totalUpcomingExpenses}
          isMobile={isMobile}
          label={buildDynamicCopy(customCopy.to_be_paid_out, filteredExpenses.length) as string}
          symbol={isMobile ? '-' : undefined}
        />

        {isMobile ? <Divider /> : <Text sx={{ fontSize: 25, lineHeight: 1 }}>=</Text>}

        <CashflowAmountHeading
          amount={availableToSpend}
          isMobile={isMobile}
          isTotal={isMobile}
          label={customCopy.remaining_cash}
          tooltip={
            <InformationTooltip
              body={customCopy.remaining_income_tooltip_body}
              title={customCopy.remaining_income_tooltip_title}
            />
          }
        />
      </Stack>

      <Drawer
        ariaLabelClose={customCopy.close_manage_income_aria}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        shouldShowHeaderBorders={false}
        title={customCopy.manage_income}
      >
        <ManageIncome />
      </Drawer>
    </Stack>
  )
}

export default observer(CashflowHeading)
